/* RESPONSIBLE TEAM: team-automation-foundations */
import {
  createConversationDataAttributeItems,
  createUserDataAttributeItems,
} from 'embercom/lib/inbox/constants';
import type ConnectionPoint from 'embercom/models/operator/visual-builder/connection-point';
import type Group from 'embercom/models/operator/visual-builder/group';
import type Step from 'embercom/models/operator/visual-builder/step';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import { stepModelClasses, stepTypes } from '../configuration-list';
import type PathConfig from 'embercom/objects/visual-builder/configuration/path';
import type IntlService from 'embercom/services/intl';
import type WorkflowConnectorInsertableActionsService from 'embercom/services/workflow-connector-insertable-actions';
import { type CreateStepParams } from 'embercom/models/operator/visual-builder/step';
import { SUPPORTED_CHANNELS } from 'embercom/lib/operator/custom-bots/constants-2';
import {
  CHANNEL_SALESFORCE_CASE,
  INTERCOM_CHANNELS_AND_EMAIL,
} from 'embercom/lib/operator/custom-bots/constants';
import type Integration from 'embercom/models/standalone/integration';
import {
  VALID_PLATFORM_ICONS,
  IntegrationTypeToPlatform,
} from 'embercom/objects/standalone/constants';
import {
  getHandoffPlatformsForApp,
  getIntegrationHandoffDisplayName,
  HANDOFF_PLATFORM_COMPONENTS,
  HANDOFF_PLATFORM_DISPLAY_NAMES,
} from 'embercom/objects/standalone/generic-handoff-platforms';

const DEFAULT_COMPONENT_TEMPLATE = 'workflows/graph-editor/popover-menu/popover-menu-item' as const;
const CATEGORY_CONFIG = {
  action: {
    iconBGClass: 'workflows__graph-editor__node-items__steps__action-icon-bg',
    iconColour: 'white',
  },
  message: {
    iconBGClass: 'workflows__graph-editor__node-items__steps__message-icon-bg',
    iconColour: 'black',
  },
  condition: {
    iconBGClass: 'workflows__graph-editor__node-items__steps__conditional-branches__if-branch-icon',
    iconColour: 'black',
  },
  existingPath: {
    iconBGClass: 'rounded-full shadow',
  },
  fin: {
    iconBGClass: 'workflows__graph-editor__node-items__steps__fin-icon-bg',
    iconColour: 'white',
  },
  neutral: {
    iconBGClass: 'workflows__graph-editor__node-items__steps__neutral-icon-bg',
    iconColour: 'black',
  },
};

const inserterMenuItems = {
  ...stepTypes,
  // extra inserter-menu categories
  endBot: 'inserter-menu-item/end-bot',
  endHandover: 'inserter-menu-item/end-handover',
  existingPaths: 'inserter-menu-item/existing-paths',
};

const ADD_CONTENT_GROUP_NAME = 'add-content-to-path';
const ADD_CONVERSATION_ACTION_GROUP_NAME = 'add-action';
const ADD_INTEGRATION_ACTION_GROUP_NAME = 'add-integration-action';
const CONNECT_EXISTING_PATH_GROUP_NAME = 'connect-existing-path';
const PROCEED_TO_ANOTHER_PATH_GROUP_NAME = 'proceed-to-another-path';
const SET_CONVERSATION_DATA_GROUP_NAME = 'set-conversation-data';
const SET_DATA_GROUP_NAME = 'set-data';
const SET_USER_DATA_GROUP_NAME = 'set-user-data';
const SUGGESTED_NEXT_STEPS_GROUP_NAME = 'suggested-next-steps';

type GroupNameType =
  | typeof SUGGESTED_NEXT_STEPS_GROUP_NAME
  | typeof ADD_CONTENT_GROUP_NAME
  | typeof ADD_CONVERSATION_ACTION_GROUP_NAME
  | typeof ADD_INTEGRATION_ACTION_GROUP_NAME
  | typeof CONNECT_EXISTING_PATH_GROUP_NAME
  | typeof PROCEED_TO_ANOTHER_PATH_GROUP_NAME
  | typeof SET_CONVERSATION_DATA_GROUP_NAME
  | typeof SET_DATA_GROUP_NAME
  | typeof SET_USER_DATA_GROUP_NAME;

type CustomHandlerFunction = (args: {
  connectionPoint: ConnectionPoint;
  editorState: EditorState;
}) => any;

type InserterMenuItemType = {
  text: string;
  value:
    | {
        customItemHandler?: CustomHandlerFunction;
        ModelClass?: typeof Step;
        stepCreationParams?: CreateStepParams;
      }
    | string;
  component: typeof DEFAULT_COMPONENT_TEMPLATE;
  groupConfig: { iconBGClass: string; iconColour?: string };
  groupName?: GroupNameType;
  icon?: string;
  svg?: string;
  tooltip?: string; // Displays a Tooltip in the inserter menu
  stamp?: { text: string; color: string }; // Displays a Stamp in the inserter menu
  onSelectItem?: () => void;
  supportedChannels?: string[];
  disabled?: boolean;
};

export {
  ADD_CONTENT_GROUP_NAME,
  ADD_CONVERSATION_ACTION_GROUP_NAME,
  ADD_INTEGRATION_ACTION_GROUP_NAME,
  CONNECT_EXISTING_PATH_GROUP_NAME,
  PROCEED_TO_ANOTHER_PATH_GROUP_NAME,
  SET_CONVERSATION_DATA_GROUP_NAME,
  SET_DATA_GROUP_NAME,
  SET_USER_DATA_GROUP_NAME,
  DEFAULT_COMPONENT_TEMPLATE,
  SUGGESTED_NEXT_STEPS_GROUP_NAME,
  CATEGORY_CONFIG,
  GroupNameType,
  inserterMenuItems,
  CustomHandlerFunction,
  InserterMenuItemType,
};

type InserterMenuRecord = Record<
  string,
  (args: {
    app: any;
    intl: IntlService;
    store: any;
    workflowConnectorInsertableActions?: WorkflowConnectorInsertableActionsService;
    attributeService: any;
    finStandaloneService: any;
    existingPaths: Group[];
    pathConfig: PathConfig;
  }) => InserterMenuItemType[] | undefined
>;

const INSERTER_MENU_ITEMS_MAP: InserterMenuRecord = {
  [inserterMenuItems.callback]: ({ intl }) => {
    return [
      {
        text: intl.t('operator.workflows.visual-builder.step-menu.callback'),
        icon: 'phone-missed',
        value: { ModelClass: stepModelClasses[stepTypes.callback] },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.action,
        groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
        supportedChannels: SUPPORTED_CHANNELS.callback,
      },
    ];
  },
  [inserterMenuItems.fin]: ({ intl }) => {
    return [
      {
        text: intl.t('operator.workflows.visual-builder.step-menu.fin'),
        icon: 'fin',
        value: { ModelClass: stepModelClasses[stepTypes.fin] },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.fin,
        groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
        'data-intercom-target': 'vbb:let-fin-answer',
        supportedChannels: SUPPORTED_CHANNELS.fin,
      },
    ];
  },
  [inserterMenuItems.applyRules]: ({ intl }) => {
    return [
      {
        text: intl.t('operator.workflows.visual-builder.step-menu.apply-rules'),
        icon: 'left',
        value: { ModelClass: stepModelClasses[stepTypes.applyRules] },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.action,
        groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
        'data-intercom-target': 'vbb:apply-rules',
        supportedChannels: SUPPORTED_CHANNELS.applyRules,
      },
    ];
  },
  [inserterMenuItems.addTagToConversation]: ({ intl }) => {
    return [
      {
        text: intl.t('operator.workflows.visual-builder.step-menu.add-tag-to-conversation'),
        icon: 'tag',
        value: { ModelClass: stepModelClasses[stepTypes.addTagToConversation] },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.action,
        groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
        supportedChannels: SUPPORTED_CHANNELS.addTagToConversation,
      },
    ];
  },
  [inserterMenuItems.removeTagFromConversation]: ({ intl }) => {
    return [
      {
        text: intl.t('operator.workflows.visual-builder.step-menu.remove-conversation-tag'),
        icon: 'tag',
        value: { ModelClass: stepModelClasses[stepTypes.removeTagFromConversation] },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.action,
        groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
        supportedChannels: SUPPORTED_CHANNELS.removeTagFromConversation,
      },
    ];
  },
  [inserterMenuItems.tagUser]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.tag-user'),
      icon: 'tag',
      value: { ModelClass: stepModelClasses[stepTypes.tagUser] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.action,
      groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
      supportedChannels: SUPPORTED_CHANNELS.tagUser,
    },
  ],
  [inserterMenuItems.removeTagFromUser]: ({ intl }) => {
    return [
      {
        text: intl.t('operator.workflows.visual-builder.step-menu.remove-person-tag'),
        icon: 'tag',
        value: { ModelClass: stepModelClasses[stepTypes.removeTagFromUser] },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.action,
        groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
        supportedChannels: SUPPORTED_CHANNELS.removeTagFromUser,
      },
    ];
  },
  [inserterMenuItems.setLanguageOverride]: ({ intl }) => {
    return [
      {
        text: intl.t('operator.workflows.visual-builder.step-menu.set-language-override'),
        icon: 'language',
        value: { ModelClass: stepModelClasses[stepTypes.setLanguageOverride] },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.action,
        groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
        supportedChannels: SUPPORTED_CHANNELS.setLanguageOverride,
      },
    ];
  },
  [inserterMenuItems.assignConversation]: ({ intl, pathConfig }) => {
    let text =
      pathConfig.constructor.name === 'NewPhoneCallTargetPathConfig'
        ? intl.t('operator.workflows.visual-builder.step-menu.hold-and-assign')
        : intl.t('operator.workflows.visual-builder.step-menu.assign-conversation');

    return [
      {
        text,
        icon: 'assignment',
        value: { ModelClass: stepModelClasses[stepTypes.assignConversation] },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.action,
        groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
        supportedChannels: SUPPORTED_CHANNELS.assignConversation,
      },
    ];
  },
  [inserterMenuItems.triggerPhoneReusableWorkflow]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.pass-to-phone-reusable-workflow'),
      icon: 'bot-filled',
      value: { ModelClass: stepModelClasses[stepTypes.triggerPhoneReusableWorkflow] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.message,
      groupName: ADD_CONTENT_GROUP_NAME,
      'data-intercom-target': 'vbb:pass-to-phone-reusable-workflow',
      supportedChannels: SUPPORTED_CHANNELS.triggerPhoneReusableWorkflow,
    },
  ],
  [inserterMenuItems.snooze]: ({ app, intl }) => {
    return (
      (<boolean>app.canUseInboxRules && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.snooze'),
          icon: 'snooze',
          value: { ModelClass: stepModelClasses[stepTypes.snooze] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.snooze,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.wait]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.wait'),
      icon: 'wait',
      value: { ModelClass: stepModelClasses[stepTypes.wait] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.action,
      groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
      supportedChannels: SUPPORTED_CHANNELS.wait,
    },
  ],
  [inserterMenuItems.waitForCallback]: ({ app, intl }) => {
    return (
      (<boolean>app.canUseWorkflowsWaitForCallback && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.wait-for-callback'),
          icon: 'wait',
          value: { ModelClass: stepModelClasses[stepTypes.waitForCallback] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.wait,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.changeConversationPriority]: ({ app, intl }) => {
    return (
      (<boolean>app.canUsePriorityRules && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.change-conversation-priority'),
          icon: 'star',
          value: { ModelClass: stepModelClasses[stepTypes.changeConversationPriority] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.changeConversationPriority as unknown as string[],
        } as InserterMenuItemType,
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.removeConversationPriority]: ({ app, intl }) => {
    return (
      (<boolean>app.canUsePriorityRules && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.remove-conversation-priority'),
          icon: 'star-outlined',
          value: { ModelClass: stepModelClasses[stepTypes.removeConversationPriority] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.removeConversationPriority as unknown as string[],
        } as InserterMenuItemType,
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.applyConversationSla]: ({ app, intl }) => {
    return (
      (<boolean>app.canUseInboxSlas && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.apply-conversation-sla'),
          icon: 'sla',
          value: { ModelClass: stepModelClasses[stepTypes.applyConversationSla] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.applyConversationSla,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.workflowConnector]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.workflow-connector'),
      icon: 'data-connectors',
      value: { ModelClass: stepModelClasses[stepTypes.workflowConnector] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.action,
      groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
      'data-intercom-target': 'vbb:custom-action',
      supportedChannels: SUPPORTED_CHANNELS.workflowConnector,
    },
  ],
  [inserterMenuItems.emailOtp]: ({ app, intl }) => {
    return (
      (app.canUseEmailOtpWorkflow && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.email-otp'),
          icon: 'locked',
          value: { ModelClass: stepModelClasses[stepTypes.emailOtp] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          'data-intercom-target': 'vbb:email-otp',
          supportedChannels: SUPPORTED_CHANNELS.emailOtp,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.note]: ({ intl, finStandaloneService, app, pathConfig }) => {
    let targetChannels =
      pathConfig.editorState.contentEditorService.activeObject?.targetChannels || [];
    let disabled =
      app.canUseSalesforceDigitalExperience &&
      targetChannels.includes(CHANNEL_SALESFORCE_CASE) &&
      !finStandaloneService.salesforcePlatform?.salesforceSetupData?.hasDigitalExperiences;
    let tooltip = disabled
      ? intl.t(
          'operator.workflows.visual-builder.step-menu.note-tooltip-digital-experiences-required',
        )
      : undefined;
    return [
      {
        text: intl.t('operator.workflows.visual-builder.step-menu.note'),
        icon: 'note',
        value: { ModelClass: stepModelClasses[stepTypes.note] },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.action,
        groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
        supportedChannels: SUPPORTED_CHANNELS.note,
        disabled,
        tooltip,
      },
    ];
  },
  [inserterMenuItems.sdrFollowupEmail]: ({ app, intl }) => {
    return (
      (<boolean>app.canUseFeature('fin-sdr-follow-up-email') && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.sdr-followup-email'),
          icon: 'email',
          value: { ModelClass: stepModelClasses[stepTypes.sdrFollowupEmail] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.sdrFollowupEmail,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.hangUp]: ({ app, intl }) => {
    return (
      (<boolean>app.canUseInboundPhoneCall && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.hang-up'),
          icon: 'phone-end',
          value: { ModelClass: stepModelClasses[stepTypes.hangUp] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.hangUp,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.finVoice]: ({ app, intl }) => {
    return (
      (app.canSetupFinVoicePhoneEasySetup && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.fin-voice'),
          icon: 'fin',
          value: { ModelClass: stepModelClasses[stepTypes.finVoice] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.finVoice,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.phoneHoldAndAssign]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.new-hold-and-assign'),
      icon: 'assignment',
      value: { ModelClass: stepModelClasses[stepTypes.phoneHoldAndAssign] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.action,
      groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
      supportedChannels: SUPPORTED_CHANNELS.phoneHoldAndAssign,
    },
  ],
  [inserterMenuItems.forwardCall]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.forward-call'),
      icon: 'phone-transfer',
      value: { ModelClass: stepModelClasses[stepTypes.forwardCall] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.action,
      groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
      supportedChannels: SUPPORTED_CHANNELS.forwardCall,
    },
  ],
  [inserterMenuItems.phoneCallCsatRequest]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.phone-call-csat-request'),
      icon: 'lwr-happy',
      value: { ModelClass: stepModelClasses[stepTypes.phoneCallCsatRequest] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.action,
      groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
      supportedChannels: SUPPORTED_CHANNELS.phoneCallCsatRequest,
    },
  ],
  [inserterMenuItems.voicemail]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.voicemail'),
      icon: 'phone-voicemail',
      value: { ModelClass: stepModelClasses[stepTypes.voicemail] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.action,
      groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
      supportedChannels: SUPPORTED_CHANNELS.voicemail,
    },
  ],
  [inserterMenuItems.phoneOneTimePasscode]: ({ app, intl }) => {
    return (
      (<boolean>app.canUsePhoneOneTimePasscodeWorkflowStep && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.phone-one-time-passcode'),
          icon: 'locked',
          value: { ModelClass: stepModelClasses[stepTypes.phoneOneTimePasscode] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.phoneOneTimePasscode,
        },
      ]) ||
      undefined
    );
  },

  [inserterMenuItems.disableComposer]: ({ app, intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.disable-composer'),
      icon: 'remove',
      value: { ModelClass: stepModelClasses[stepTypes.disableComposer] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.action,
      groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
      'data-intercom-target': 'vbb:disable-customer-reply',
      supportedChannels: app.canUseFeature('team-rd-services-disable-composer-email-channel')
        ? [...INTERCOM_CHANNELS_AND_EMAIL]
        : SUPPORTED_CHANNELS.disableComposer,
    },
  ],
  [inserterMenuItems.closeConversation]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.close-conversation'),
      icon: 'check',
      value: { ModelClass: stepModelClasses[stepTypes.closeConversation] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.action,
      groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
      supportedChannels: SUPPORTED_CHANNELS.closeConversation,
    },
  ],
  [inserterMenuItems.setTicketCustomState]: ({ intl }) => {
    return [
      {
        text: intl.t('operator.workflows.visual-builder.step-menu.set-ticket-custom-state'),
        icon: 'ticket',
        value: { ModelClass: stepModelClasses[stepTypes.setTicketCustomState] },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.action,
        groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
        supportedChannels: SUPPORTED_CHANNELS.setTicketCustomState,
      },
    ];
  },
  [inserterMenuItems.summarizeConversation]: ({ app, intl }) => {
    let isAutoSummarizeAvailable =
      app.hasSummarizationSettingEnabled || app.hasTextTransformationSettingEnabled;

    return (
      (isAutoSummarizeAvailable && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.summarize-conversation'),
          icon: 'ai',
          value: { ModelClass: stepModelClasses[stepTypes.summarizeConversation] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          'data-intercom-target': 'vbb:summarize-conversation',
          tooltip: intl.t(
            'operator.workflows.visual-builder.step-menu.summarize-conversation-tooltip',
          ),
          supportedChannels: SUPPORTED_CHANNELS.summarizeConversation,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.classifyConversationAttribute]: ({ app, intl }) => {
    let isClassificationAvailabile = app.canUseAiConversationClassification;

    return (
      (isClassificationAvailabile && [
        {
          text: intl.t(
            'operator.workflows.visual-builder.step-menu.classify-conversation-attribute',
          ),
          icon: 'ai',
          value: { ModelClass: stepModelClasses[stepTypes.classifyConversationAttribute] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          tooltip: intl.t(
            'operator.workflows.visual-builder.step-menu.classify-conversation-attribute-tooltip',
          ),
          stamp: {
            text: intl.t('operator.workflows.visual-builder.step-menu.beta-stamp'),
            color: 'blue',
          },
          supportedChannels: SUPPORTED_CHANNELS.classifyConversationAttribute,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.chatMessage]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.chat-message'),
      icon: 'chat-filled',
      value: { ModelClass: stepModelClasses[stepTypes.chatMessage] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.message,
      groupName: ADD_CONTENT_GROUP_NAME,
      supportedChannels: SUPPORTED_CHANNELS.chatMessage,
    },
  ],
  [inserterMenuItems.convertToTicket]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.convert-to-ticket'),
      icon: 'ticket',
      value: { ModelClass: stepModelClasses[stepTypes.convertToTicket] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.message,
      groupName: ADD_CONTENT_GROUP_NAME,
      supportedChannels: SUPPORTED_CHANNELS.convertToTicket,
      tooltip: intl.t('operator.workflows.visual-builder.step-menu.convert-to-ticket-tooltip'),
    },
  ],
  [inserterMenuItems.attributeCollector]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.attribute-collector'),
      icon: 'chat-bubble',
      value: { ModelClass: stepModelClasses[stepTypes.attributeCollector] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.message,
      groupName: ADD_CONTENT_GROUP_NAME,
      supportedChannels: SUPPORTED_CHANNELS.attributeCollector,
    },
  ],
  [inserterMenuItems.triggerWorkflow]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.trigger-workflow'),
      icon: 'bot-filled',
      value: { ModelClass: stepModelClasses[stepTypes.triggerWorkflow] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.message,
      groupName: ADD_CONTENT_GROUP_NAME,
      'data-intercom-target': 'vbb:pass-to-another-bot',
      supportedChannels: SUPPORTED_CHANNELS.triggerWorkflow,
    },
  ],
  [inserterMenuItems.setExpectations]: ({ intl, pathConfig }) => {
    let text =
      pathConfig.constructor.name === 'NewPhoneCallTargetPathConfig'
        ? intl.t('operator.workflows.visual-builder.step-menu.expected-hold-time')
        : intl.t('operator.workflows.visual-builder.step-menu.set-expectations');

    return [
      {
        text,
        icon: 'clock',
        value: { ModelClass: stepModelClasses[stepTypes.setExpectations] },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.message,
        groupName: ADD_CONTENT_GROUP_NAME,
        supportedChannels: SUPPORTED_CHANNELS.setExpectations,
      },
    ];
  },
  [inserterMenuItems.freeInput]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.free-input'),
      icon: 'chat-bubble',
      value: { ModelClass: stepModelClasses[stepTypes.freeInput] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.message,
      groupName: ADD_CONTENT_GROUP_NAME,
      supportedChannels: SUPPORTED_CHANNELS.freeInput,
    },
  ],
  [inserterMenuItems.conversationRatings]: ({ intl, app }) => {
    let tooltip = intl.t('operator.workflows.visual-builder.step-menu.workflow-ratings-tooltip');
    let dataTarget = 'vbb:workflows-csat';

    return (
      (<boolean>app.canUseConversationRatings && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.conversation-ratings'),
          icon: 'lwr-happy',
          value: { ModelClass: stepModelClasses[stepTypes.conversationRatings] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.message,
          groupName: ADD_CONTENT_GROUP_NAME,
          tooltip,
          'data-intercom-target': dataTarget,
          supportedChannels: SUPPORTED_CHANNELS.conversationRatings,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.sendTicket]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.send-ticket'),
      icon: 'ticket',
      value: { ModelClass: stepModelClasses[stepTypes.sendTicket] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.message,
      groupName: ADD_CONTENT_GROUP_NAME,
      supportedChannels: SUPPORTED_CHANNELS.sendTicket,
    },
  ],
  [inserterMenuItems.messengerApp]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.messenger-app'),
      icon: 'app-store',
      value: { ModelClass: stepModelClasses[stepTypes.messengerApp] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.message,
      groupName: ADD_CONTENT_GROUP_NAME,
      supportedChannels: SUPPORTED_CHANNELS.messengerApp,
    },
  ],
  [inserterMenuItems.replyButtons]: ({ intl, pathConfig }) => {
    let isForPhoneChannel = pathConfig.constructor.name === 'NewPhoneCallTargetPathConfig';
    let text = isForPhoneChannel
      ? intl.t('operator.workflows.visual-builder.step-menu.reply-options')
      : intl.t('operator.workflows.visual-builder.step-menu.reply-buttons');
    let icon =
      pathConfig.constructor.name === 'NewPhoneCallTargetPathConfig'
        ? 'phone-keypad'
        : 'button-pill';

    return [
      {
        text,
        icon,
        value: {
          ModelClass: stepModelClasses[stepTypes.replyButtons],
          stepCreationParams: { createEmptyBlock: isForPhoneChannel },
        },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.condition,
        groupName: PROCEED_TO_ANOTHER_PATH_GROUP_NAME,
        supportedChannels: SUPPORTED_CHANNELS.replyButtons,
      },
    ];
  },
  [inserterMenuItems.customObjectSelector]: ({ intl, pathConfig }) => {
    let text =
      pathConfig.constructor.name === 'NewPhoneCallTargetPathConfig'
        ? intl.t('operator.workflows.visual-builder.step-menu.reply-custom-object-options')
        : intl.t('operator.workflows.visual-builder.step-menu.custom-object-selector');
    let icon =
      pathConfig.constructor.name === 'NewPhoneCallTargetPathConfig'
        ? 'phone-keypad'
        : 'button-pill';

    return [
      {
        text,
        icon,
        value: { ModelClass: stepModelClasses[stepTypes.customObjectSelector] },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.condition,
        groupName: PROCEED_TO_ANOTHER_PATH_GROUP_NAME,
        supportedChannels: SUPPORTED_CHANNELS.customObjectSelector,
      },
    ];
  },
  [inserterMenuItems.resolutionRules]: ({ intl, app }) => {
    return (
      (<boolean>app.canUseFinFirstAiCategories && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.outcome-limit'),
          icon: 'branch',
          value: { ModelClass: stepModelClasses[stepTypes.resolutionRules] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.condition,
          groupName: PROCEED_TO_ANOTHER_PATH_GROUP_NAME,
          'data-intercom-target': 'vbb:resolution-rules',
          supportedChannels: SUPPORTED_CHANNELS.conditionalBranches,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.conditionalBranches]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.conditional-branches'),
      icon: 'branch',
      value: { ModelClass: stepModelClasses[stepTypes.conditionalBranches] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.condition,
      groupName: PROCEED_TO_ANOTHER_PATH_GROUP_NAME,
      'data-intercom-target': 'vbb:conditional-branch',
      supportedChannels: SUPPORTED_CHANNELS.conditionalBranches,
    },
  ],
  [inserterMenuItems.escalationRuleTriggered]: ({ intl, app }) => {
    return (
      (<boolean>app.canUseFinFirstAiCategories && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.escalation-rule-triggered'),
          icon: 'fin',
          value: { ModelClass: stepModelClasses[stepTypes.escalationRuleTriggered] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.condition,
          groupName: PROCEED_TO_ANOTHER_PATH_GROUP_NAME,
          'data-intercom-target': 'vbb:escalation-rule-triggered',
          supportedChannels: SUPPORTED_CHANNELS.escalationRuleTriggered,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.endBot]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.end-bot'),
      icon: 'stop',
      value: {
        customItemHandler: ({ connectionPoint }) => (connectionPoint.isTerminal = true),
      },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.action,
      groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
    },
  ],
  [inserterMenuItems.endHandover]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.end-handover'),
      icon: 'stop',
      value: {
        customItemHandler: ({ connectionPoint }) => (connectionPoint.isTerminal = true),
      },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.action,
      groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
    },
  ],
  [inserterMenuItems.answerTerminal]: ({ intl }) => [
    {
      text: intl.t('operator.workflows.visual-builder.step-menu.answer-terminal'),
      icon: 'respond',
      value: { ModelClass: stepModelClasses[stepTypes.answerTerminal] },
      component: DEFAULT_COMPONENT_TEMPLATE,
      groupConfig: CATEGORY_CONFIG.action,
      groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
      supportedChannels: SUPPORTED_CHANNELS.answerTerminal,
    },
  ],
  [inserterMenuItems.sendToSalesforce]: ({ app, intl }) => {
    return (
      (<boolean>app.hasSalesforceIntegration && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.send-to-salesforce'),
          icon: 'salesforce',
          value: { ModelClass: stepModelClasses[stepTypes.sendToSalesforce] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_INTEGRATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.sendToSalesforce,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.createSalesforceCase]: ({ app, intl }) => {
    return (
      (<boolean>app.hasSalesforceIntegration && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.create-salesforce-case'),
          icon: 'salesforce',
          value: { ModelClass: stepModelClasses[stepTypes.createSalesforceCase] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_INTEGRATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.createSalesforceCase,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.sendToHubspot]: ({ app, intl }) => {
    return (
      (<boolean>app.hasHubSpotIntegration && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.send-to-hubspot'),
          icon: 'hubspot',
          value: { ModelClass: stepModelClasses[stepTypes.sendToHubspot] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_INTEGRATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.sendToHubspot,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.customActionTemplate]: ({ workflowConnectorInsertableActions }) => {
    let customActions = workflowConnectorInsertableActions!.actions;
    let customActionTemplates = customActions.filter((action: any) => action.isTemplate);

    return customActionTemplates.map((template: any) => {
      return {
        text: template.name,
        icon: 'data-connectors',
        value: {
          ModelClass: stepModelClasses[stepTypes.workflowConnector],
          stepCreationParams: { customAction: template, listTemplates: true },
        },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.action,
        groupName: ADD_INTEGRATION_ACTION_GROUP_NAME,
        supportedChannels: SUPPORTED_CHANNELS.customActionTemplate,
      };
    }, this);
  },
  [inserterMenuItems.sendToMarketo]: ({ app, intl }) => {
    return (
      (<boolean>app.hasMarketoEnrichmentIntegration && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.send-to-marketo'),
          icon: 'marketo',
          value: { ModelClass: stepModelClasses[stepTypes.sendToMarketo] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_INTEGRATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.sendToMarketo,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.triggerMarketoCampaign]: ({ app, intl }) => {
    return (
      (<boolean>app.hasMarketoEnrichmentIntegration && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.trigger-marketo-campaign'),
          icon: 'marketo',
          value: { ModelClass: stepModelClasses[stepTypes.triggerMarketoCampaign] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_INTEGRATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.triggerMarketoCampaign,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.notifySlackChannel]: ({ app, intl }) => {
    return (
      (<boolean>(app.hasSlackNotificationsIntegration || app.hasSlackIntegration) && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.notify-slack-channel'),
          icon: 'slack',
          value: { ModelClass: stepModelClasses[stepTypes.notifySlackChannel] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_INTEGRATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.notifySlackChannel,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.setConversationDataAttribute]: ({
    app,
    intl,
    attributeService,
    store,
    finStandaloneService,
  }) => {
    let conversationAttributes = attributeService.visualBotBuilderSetConversationDataAttributes;

    // Filter out CRM fields that are neither createable nor updateable
    // (e.g., Salesforce "Case Number"). These read-only system fields cannot be set in workflows.
    if (app.canUseStandalone && app.hasStandalonePlatform('salesforce')) {
      let salesforcePlatform = finStandaloneService.salesforcePlatform;
      // Get external field identifiers from the store
      let externalFields = store.peekAll('standalone/external-field-identifier');

      // Only filter if external fields have been loaded
      if (externalFields && externalFields.length > 0) {
        conversationAttributes = conversationAttributes.filter((attribute: any) => {
          // Check if this conversation attribute is mapped to an external Salesforce field
          let externalField = salesforcePlatform.findMappedConversationField(attribute.identifier);

          if (!externalField) {
            // Not mapped to an external field, include it
            return true;
          }

          // Include only if the field is createable or updateable
          return externalField.createable || externalField.updateable;
        });
      }
    }

    let conversationCustomAttributeItems = createConversationDataAttributeItems(
      conversationAttributes,
    ) as { name: string; attribute_identifier: string; icon: string; cda_id: string }[];
    return conversationCustomAttributeItems.map((attr) => {
      let icon = 'conversation';

      if (app.canUseStandalone && VALID_PLATFORM_ICONS.includes(attr.icon)) {
        icon = attr.icon;
      }

      return {
        text: intl.t(
          'operator.workflows.visual-builder.step-menu.set-conversation-data-attribute',
          {
            name: attr.name,
          },
        ),
        icon,
        value: {
          ModelClass: stepModelClasses[stepTypes.setConversationDataAttribute],
          stepCreationParams: {
            attributeIdentifier: attr.attribute_identifier,
            cdaId: attr.cda_id,
          },
        },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.action,
        groupName:
          app.canUseStandalone && app.hasStandalonePlatform('salesforce')
            ? SET_DATA_GROUP_NAME
            : SET_CONVERSATION_DATA_GROUP_NAME,
        supportedChannels: SUPPORTED_CHANNELS.setConversationDataAttribute,
      };
    }, this);
  },
  [inserterMenuItems.setUserDataAttribute]: ({ intl, attributeService }) => {
    let userDataAttributeItems = createUserDataAttributeItems(
      attributeService.visualBotBuilderSetUserDataAttributes,
    ) as { name: string; attribute_identifier: string }[];
    return userDataAttributeItems.map((attr) => {
      return {
        text: intl.t('operator.workflows.visual-builder.step-menu.set-user-data-attribute', {
          name: attr.name,
        }),
        icon: 'person',
        value: {
          ModelClass: stepModelClasses[stepTypes.setUserDataAttribute],
          stepCreationParams: { attributeIdentifier: attr.attribute_identifier },
        },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.action,
        groupName: SET_USER_DATA_GROUP_NAME,
        supportedChannels: SUPPORTED_CHANNELS.setUserDataAttribute,
      };
    }, this);
  },
  [inserterMenuItems.existingPaths]: ({ existingPaths }) => {
    return existingPaths.reduce((existingPathItems, group) => {
      return existingPathItems.concat({
        text: group.fullPathTitle,
        svg: 'series/edge-add-arrow',
        value: {
          customItemHandler: ({ connectionPoint, editorState }) =>
            editorState.createAndRenderEdge({
              toGroup: group,
              outwardConnectionPoint: connectionPoint,
            }),
        },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.existingPath,
        groupName: CONNECT_EXISTING_PATH_GROUP_NAME,
      });
    }, [] as InserterMenuItemType[]);
  },
  [inserterMenuItems.finCustom]: ({ app, intl, pathConfig }) => {
    let inFinTask = pathConfig.editorState.workflow.isAiAgentWorkflow;
    return (
      (<boolean>app.canUseFinCustomStep && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.fin-custom'),
          icon: 'fin',
          value: {
            ModelClass: stepModelClasses[stepTypes.finCustom],
            stepCreationParams: { inFinTask },
          },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.finCustom,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.handoffToZendeskTicket]: ({ app, intl }) => {
    return (
      (<boolean>(app.canUseStandalone && app.hasStandalonePlatform('zendesk')) && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.create-zendesk-ticket'),
          icon: 'zendesk',
          tooltip: intl.t(
            'operator.workflows.visual-builder.step-menu.create-zendesk-ticket-tooltip',
          ),
          value: { ModelClass: stepModelClasses[stepTypes.handoffToZendeskTicket] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.handoffToZendeskTicket,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.handoffToExternalTicket]: ({ intl, store, app }) => {
    if (!app.isStandaloneApp) {
      return undefined;
    }

    let handoffIntegrations = store.peekAll('standalone/integration');

    if (!handoffIntegrations || handoffIntegrations.length === 0) {
      return getHandoffPlatformsForApp(app).map((platform) => {
        let displayName = HANDOFF_PLATFORM_DISPLAY_NAMES[platform];

        return {
          text: intl.t(`operator.workflows.visual-builder.create-external-ticket.menu-item`, {
            integration: displayName,
          }),
          icon: 'ticket',
          tooltip: intl.t(`operator.workflows.visual-builder.create-external-ticket.tooltip`, {
            integration: displayName,
          }),
          value: {
            ModelClass: stepModelClasses[stepTypes.handoffToExternalTicket],
            stepCreationParams: { integrationType: platform },
          },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.handoffToExternalTicketGeneric,
        };
      });
    } else {
      handoffIntegrations = handoffIntegrations.filter(
        (integration: Integration) =>
          integration.supportsExternalTicketHandoverFromChannels.length > 0,
      );

      return handoffIntegrations.map(
        (integration: {
          integrationType: string;
          integrationId: number;
          displayName: string;
          supportsExternalTicketHandoverFromChannels: Array<string>;
        }) => {
          let integrationType = integration.integrationType;
          let integrationId = integration.integrationId;
          let displayName =
            getIntegrationHandoffDisplayName(Number(integrationType)) ?? integration.displayName;
          // Supported channels are now returned in supportsExternalTicketHandoverFromChannels from the integration
          let supportedChannels = integration.supportsExternalTicketHandoverFromChannels;

          // For platforms with a generic handoff sidesheet (e.g. Front, Zendesk),
          // use the string platform name so the sidesheet component is resolved correctly.
          let platform =
            IntegrationTypeToPlatform[
              Number(integrationType) as keyof typeof IntegrationTypeToPlatform
            ];
          let isGenericHandoffPlatform =
            platform &&
            HANDOFF_PLATFORM_COMPONENTS[platform as keyof typeof HANDOFF_PLATFORM_COMPONENTS];

          let translationKey = isGenericHandoffPlatform
            ? 'create-external-ticket'
            : 'handoff-to-external-ticket';

          if (isGenericHandoffPlatform) {
            displayName =
              HANDOFF_PLATFORM_DISPLAY_NAMES[
                platform as keyof typeof HANDOFF_PLATFORM_DISPLAY_NAMES
              ] ?? displayName;
            supportedChannels = [...SUPPORTED_CHANNELS.handoffToExternalTicketGeneric];
          }

          return {
            text: intl.t(`operator.workflows.visual-builder.${translationKey}.menu-item`, {
              integration: displayName,
            }),
            icon: 'ticket',
            tooltip: intl.t(`operator.workflows.visual-builder.${translationKey}.tooltip`, {
              integration: displayName,
            }),
            value: {
              ModelClass: stepModelClasses[stepTypes.handoffToExternalTicket],
              stepCreationParams: isGenericHandoffPlatform
                ? { integrationType: platform }
                : { integrationType, integrationId },
            },
            component: DEFAULT_COMPONENT_TEMPLATE,
            groupConfig: CATEGORY_CONFIG.action,
            groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
            supportedChannels,
          };
        },
      );
    }
  },
  [inserterMenuItems.handoffToExternalAgent]: ({ intl, store, app }) => {
    if (!app.isStandaloneApp) {
      return undefined;
    }

    let handoffIntegrations = store.peekAll('standalone/integration');

    if (!handoffIntegrations || handoffIntegrations.length === 0) {
      return undefined;
    }

    handoffIntegrations = handoffIntegrations.filter(
      (integration: Integration) =>
        integration.supportsExternalAgentHandoverFromChannels.length > 0,
    );

    return handoffIntegrations.map(
      (integration: {
        integrationType: string;
        integrationId: number;
        displayName: string;
        supportsExternalAgentHandoverFromChannels: Array<string>;
      }) => {
        let integrationType = integration.integrationType;
        let integrationId = integration.integrationId;
        let displayName =
          getIntegrationHandoffDisplayName(Number(integrationType)) ?? integration.displayName;
        // Supported channels are now returned in supportsExternalAgentHandoverFromChannels from the integration
        let supportedChannels = integration.supportsExternalAgentHandoverFromChannels;

        return {
          text: intl.t(`operator.workflows.visual-builder.handoff-to-external-agent.menu-item`, {
            integration: displayName,
          }),
          icon: 'new-direct-message',
          tooltip: intl.t(`operator.workflows.visual-builder.handoff-to-external-agent.tooltip`, {
            integration: displayName,
          }),
          value: {
            ModelClass: stepModelClasses[stepTypes.handoffToExternalAgent],
            stepCreationParams: { integrationType, integrationId },
          },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          supportedChannels,
        };
      },
    );
  },
  [inserterMenuItems.handoffToSalesforceCase]: ({ app, intl }) => {
    return (
      (<boolean>(app.canUseStandalone && app.hasStandalonePlatform('salesforce')) && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.handoff-to-salesforce-case'),
          svg: 'standalone/handoff-to-salesforce-case',
          value: { ModelClass: stepModelClasses[stepTypes.handoffToSalesforceCase] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.handoffToSalesforceCase,
          tooltip: intl.t(
            'operator.workflows.visual-builder.step-menu.handoff-to-salesforce-case-tooltip',
            {
              htmlSafe: true,
            },
          ),
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.handoffToSalesforceChat]: ({ app, intl }) => {
    return (
      (<boolean>(app.canUseStandalone && app.hasStandalonePlatform('salesforce')) &&
        app.canUseHandoffToSalesforceChat && [
          {
            text: intl.t('operator.workflows.visual-builder.step-menu.handoff-to-salesforce-chat'),
            icon: 'new-direct-message',
            value: { ModelClass: stepModelClasses[stepTypes.handoffToSalesforceChat] },
            component: DEFAULT_COMPONENT_TEMPLATE,
            groupConfig: CATEGORY_CONFIG.action,
            groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
            supportedChannels: SUPPORTED_CHANNELS.handoffToSalesforceChat,
            tooltip: intl.t(
              'operator.workflows.visual-builder.step-menu.handoff-to-salesforce-chat-tooltip',
              {
                htmlSafe: true,
              },
            ),
          },
        ]) ||
      undefined
    );
  },
  [inserterMenuItems.handoffToSalesforceInAppMessaging]: ({ app, intl }) => {
    return (
      (<boolean>(app.canUseStandalone && app.hasStandalonePlatform('salesforce')) && [
        {
          text: intl.t(
            'operator.workflows.visual-builder.step-menu.handoff-to-salesforce-in-app-messaging',
          ),
          icon: 'new-direct-message',
          value: { ModelClass: stepModelClasses[stepTypes.handoffToSalesforceInAppMessaging] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.handoffToSalesforceInAppMessaging,
          tooltip: intl.t(
            'operator.workflows.visual-builder.step-menu.handoff-to-salesforce-in-app-messaging-tooltip',
            {
              htmlSafe: true,
            },
          ),
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.handoffToSalesforceTeam]: ({ app, intl }) => {
    return (
      (<boolean>(app.canUseStandalone && app.hasStandalonePlatform('salesforce')) && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.handoff-to-salesforce-team'),
          svg: 'standalone/handoff-to-salesforce-team',
          value: { ModelClass: stepModelClasses[stepTypes.handoffToSalesforceTeam] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          supportedChannels: SUPPORTED_CHANNELS.handoffToSalesforceTeam,
          tooltip: intl.t(
            'operator.workflows.visual-builder.step-menu.handoff-to-salesforce-team-tooltip',
          ),
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.handoffToUrl]: ({ app, intl }) => {
    return (
      (<boolean>app.isStandaloneApp && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.handoff-to-url'),
          icon: 'new-window',
          value: { ModelClass: stepModelClasses[stepTypes.handoffToUrl] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          tooltip: intl.t('operator.workflows.visual-builder.step-menu.handoff-to-url-tooltip', {
            htmlSafe: true,
          }),
          supportedChannels: SUPPORTED_CHANNELS.handoffToUrl,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.handoffWithJavaScript]: ({ app, intl }) => {
    return (
      (<boolean>app.isStandaloneApp && [
        {
          text: intl.t('operator.workflows.visual-builder.step-menu.handoff-with-javascript'),
          icon: 'code',
          value: { ModelClass: stepModelClasses[stepTypes.handoffWithJavaScript] },
          component: DEFAULT_COMPONENT_TEMPLATE,
          groupConfig: CATEGORY_CONFIG.action,
          groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
          tooltip: intl.t(
            'operator.workflows.visual-builder.step-menu.handoff-with-javascript-tooltip',
            {
              htmlSafe: true,
            },
          ),
          supportedChannels: SUPPORTED_CHANNELS.handoffWithJavaScript,
        },
      ]) ||
      undefined
    );
  },
  [inserterMenuItems.handoffToZendeskAgent]: ({ app, intl, pathConfig }) => {
    if (!(app.canUseStandalone && app.hasStandalonePlatform('zendesk'))) {
      return undefined;
    }

    let tooltip = undefined;
    if (pathConfig.constructor.name === 'NewZendeskTicketPathConfig') {
      tooltip = intl.t(
        'operator.workflows.visual-builder.step-menu.handoff-to-zendesk-agent-tooltip-zd-tickets',
      );
    } else if (pathConfig.constructor.name === 'NewZendeskSunshineConversationPathConfig') {
      tooltip = intl.t(
        'operator.workflows.visual-builder.step-menu.handoff-to-zendesk-agent-tooltip-zd-messaging',
      );
    } else if (pathConfig.constructor.name === 'StandaloneInboundPathConfig') {
      tooltip = intl.t(
        'operator.workflows.visual-builder.step-menu.handoff-to-zendesk-agent-tooltip-ic-messenger',
      );
    }

    return [
      {
        text: intl.t('operator.workflows.visual-builder.step-menu.handoff-to-zendesk-agent'),
        icon: 'zendesk',
        tooltip,
        value: { ModelClass: stepModelClasses[stepTypes.handoffToZendeskAgent] },
        component: DEFAULT_COMPONENT_TEMPLATE,
        groupConfig: CATEGORY_CONFIG.action,
        groupName: ADD_CONVERSATION_ACTION_GROUP_NAME,
        supportedChannels: SUPPORTED_CHANNELS.handoffToZendeskAgent,
      },
    ];
  },
};

export default INSERTER_MENU_ITEMS_MAP;
